import { Link } from 'react-router-dom';
import { TestimonialCard } from '../components/TestimonialCard';
import { useThemeArt } from '../hooks/useThemeArt';
import { getSite } from '../lib/site';
import { featuredTestimonials, getTestimonials } from '../lib/testimonials';

export function Home() {
  const site = getSite();
  const art = useThemeArt();
  const testimonials = featuredTestimonials(3);
  const tIntro = getTestimonials();

  return (
    <>
      <section className="hero-fs" aria-label="Welcome">
        <div className="hero-fs-media">
          <picture>
            <source media="(max-width: 720px)" srcSet={art.heroMobile} />
            <img
              className="hero-fs-img"
              src={art.hero}
              alt="Creative music studio — piano, guitar, and color in the air"
              width={1280}
              height={720}
              fetchPriority="high"
              decoding="async"
            />
          </picture>
        </div>
        <div className="hero-fs-scrim" aria-hidden />
        <div className="hero-fs-vignette" aria-hidden />
        <div className="hero-fs-content">
          <p className="hero-kicker">
            <span className="hero-kicker-dot" aria-hidden />
            {site.eyebrow}
          </p>
          <h1 className="hero-display">
            <span className="hero-display-name">Miss Natalie</span>
          </h1>
          <p className="hero-display-line">{site.hero.headline}</p>
          <p className="hero-display-lede">{site.hero.lede}</p>
          <div className="btn-row hero-photo-actions">
            <Link className="btn btn-glow" to="/contact">
              Request a lesson
            </Link>
            <Link className="btn btn-glass" to="/lessons">
              Voice · Piano · Guitar
            </Link>
          </div>
        </div>
        <a className="hero-scroll" href="#studio-path" aria-label="Scroll to content">
          <span>Scroll</span>
          <span className="hero-scroll-arrow" aria-hidden>
            ↓
          </span>
        </a>
      </section>

      <div className="marquee" aria-hidden>
        <div className="marquee-track">
          {Array.from({ length: 2 }).map((_, i) => (
            <span key={i} className="marquee-chunk">
              Voice ✦ Piano ✦ Guitar ✦ Recitals ✦ K–5 joy ✦ Winter Park ✦ Personalized ✦ Creative ✦
            </span>
          ))}
        </div>
      </div>

      <section className="section band-cream" id="studio-path">
        <div className="wrap split-feature">
          <div className="split-feature-media">
            <img
              src={art.instruments}
              alt="Artistic collage of voice, piano, and guitar"
              width={864}
              height={1152}
              loading="lazy"
            />
            <div className="split-feature-frame" aria-hidden />
          </div>
          <div className="split-feature-copy">
            <p className="section-kicker">The studio</p>
            <h2 className="display-lg">Three pathways. One joyful sound.</h2>
            <p className="lead">
              Creative, personalized music for elementary hearts — not worksheets, not one-size-fits-all.
              Songs they own. Technique that sticks. Confidence they can feel on stage.
            </p>
            <ol className="path-list">
              {site.instruments.map((item, i) => (
                <li key={item.id}>
                  <span className="path-num">0{i + 1}</span>
                  <div>
                    <strong>{item.name}</strong>
                    <span>{item.tagline}</span>
                  </div>
                </li>
              ))}
            </ol>
            <div className="btn-row">
              <Link className="btn btn-primary" to="/lessons">
                Explore lessons
              </Link>
              <Link className="btn btn-ghost" to="/about">
                Meet Miss Natalie
              </Link>
              <Link className="btn btn-ghost" to="/studio">
                Art themes
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section wrap">
        <div className="section-head section-head-center">
          <p className="section-kicker">How families learn</p>
          <h2 className="display-lg">Monarch energy. Private care.</h2>
        </div>
        <div className="mosaic">
          {site.offerings.map((o, i) => (
            <article key={o.id} className={`mosaic-card mosaic-card-${i + 1}`}>
              <p className="offer-where">{o.where}</p>
              <h3>{o.name}</h3>
              <p>{o.body}</p>
              <ul>
                {o.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </article>
          ))}
          <div className="mosaic-stat">
            <strong>30+</strong>
            <span>years of creative teaching</span>
          </div>
          <div className="mosaic-stat mosaic-stat-alt">
            <strong>100s</strong>
            <span>happy students &amp; families</span>
          </div>
        </div>
        <div className="btn-row" style={{ justifyContent: 'center', marginTop: '1.75rem' }}>
          <Link className="btn btn-secondary" to="/classes">
            Classes &amp; private
          </Link>
          <Link className="btn btn-ghost" to="/camp">
            Summer camp preview
          </Link>
        </div>
      </section>

      <section className="stage-band" aria-label="Recitals and stories">
        <img
          className="stage-band-img"
          src={art.stage}
          alt=""
          width={1280}
          height={720}
          loading="lazy"
        />
        <div className="stage-band-scrim" />
        <div className="wrap stage-band-content">
          <p className="section-kicker section-kicker-light">The spotlight moments</p>
          <h2 className="display-lg display-on-dark">
            Recitals. Performances. Graduates who keep singing.
          </h2>
          <p className="lead lead-on-dark">
            From first shaky notes to middle-school choirs and talent-show bows — the long game of joy.
          </p>
          <Link className="btn btn-glow" to="/stories">
            Read the stories
          </Link>
        </div>
      </section>

      <section className="section wrap">
        <div className="section-head">
          <p className="section-kicker">Kind words</p>
          <h2 className="display-lg">{tIntro.headline}</h2>
          <p>{tIntro.intro}</p>
        </div>
        <div className="testimonial-grid">
          {testimonials.map((item) => (
            <TestimonialCard key={item.id} item={item} />
          ))}
        </div>
        <div className="btn-row" style={{ marginTop: '1.25rem' }}>
          <Link className="btn btn-secondary" to="/stories">
            All sample stories
          </Link>
        </div>
      </section>

      <section className="section wrap">
        <div className="cta-splash">
          <div className="cta-splash-blob" aria-hidden />
          <div className="cta-splash-inner">
            <h2 className="display-lg">Give your child a musical home base</h2>
            <p>
              K–5 focus. Creative teaching. Monarch in Winter Park or private lessons — wherever they
              bloom best.
            </p>
            <Link className="btn btn-glow" to="/contact">
              Get started
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
